import type {
  AccessBulkAction,
  AccessBulkResponse,
  AccessCatalogItemResponse,
  AccessCatalogListResponse,
  AccessLevelListResponse,
  AccessLevelResponse,
  AccessNodeListResponse,
  AccessNodeResponse,
  AccessPasswordHandling,
  AccessQueueResponse,
  AccessQueueType,
  AccessRecordListResponse,
  AccessRecordResponse,
  AccessRecordStatus,
  AccessRequestDecisionResponse,
  AccessRequestListResponse,
  AccessRequestResponse,
  AccessRequestStatus,
  AccessSettingsResponse,
  AdminMailEmailListResponse,
  AdminOtherAccessRequestListResponse,
  AdminSessionsResponse,
  AnnouncementListResponse,
  AnnouncementResponse,
  AnnouncementTypeListResponse,
  AnnouncementTypeResponse,
  AnswerEmailListResponse,
  ApiErrorBody,
  ApiErrorCode,
  ApplicationPublic,
  ApplicationResponse,
  ApplicationsSummary,
  ApplicationTagListResponse,
  ApplicationTagResponse,
  ApproveOtherAccessRequestRequest,
  ApproveOtherAccessRequestResponse,
  AuditEntryPublic,
  AuditExportResponse,
  AuditGroup,
  BellNotificationListResponse,
  BellNotificationReadAllResponse,
  BellNotificationReadResponse,
  BrandingPublic,
  BrandingResponse,
  BulkGrantRequest,
  BulkGrantResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  CreateAnnouncementRequest,
  CreateAnnouncementTypeRequest,
  CreateApplicationRequest,
  CreateApplicationTagRequest,
  CreateGrantRequest,
  CreateOtherAccessRequestRequest,
  CreateUserRequest,
  CreateUserResponse,
  GrantListResponse,
  GrantPublic,
  IssueApplicationCredentialResponse,
  ItEmailAction,
  ItEmailListResponse,
  ItEmailMembershipListResponse,
  ItEmailResponse,
  ItEmailStatus,
  LauncherAnnouncementsResponse,
  LauncherApplicationsResponse,
  LoginRequest,
  LoginResponse,
  MyPreferencesResponse,
  MySessionsResponse,
  OperationalSettingsResponse,
  OtherAccessRequestListResponse,
  OtherAccessRequestResponse,
  OtherAccessRequestStatus,
  Paginated,
  PaginatedWithSummary,
  RedirectTargetResponse,
  ResetPasswordResponse,
  SessionRefusalReason,
  SessionUser,
  TestApplicationConnectionRequest,
  TestApplicationConnectionResponse,
  TwoFactorDisableRequest,
  TwoFactorDisableResponse,
  TwoFactorEnrollBeginResponse,
  TwoFactorEnrollConfirmRequest,
  TwoFactorEnrollConfirmResponse,
  TwoFactorRecoveryRequest,
  TwoFactorRecoveryResponse,
  TwoFactorRegenerateRecoveryCodesRequest,
  TwoFactorRegenerateRecoveryCodesResponse,
  TwoFactorStatusResponse,
  TwoFactorVerifyRequest,
  UpdateAccessSettingsRequest,
  UpdateAnnouncementRequest,
  UpdateAnnouncementTypeRequest,
  UpdateApplicationRequest,
  UpdateApplicationTagRequest,
  UpdateBrandingRequest,
  UpdateMyPreferencesRequest,
  UpdateOperationalSettingsRequest,
  UpdateUserRequest,
  UserPublic,
  UserResponse,
  UsersSummary,
} from '@accounts/shared';

import { STR } from '../i18n';

import { noteSessionEstablished, noteUnauthenticated } from './bounce';

/**
 * The typed fetch client for the Accounts API. Every request is same-origin (via the Vite
 * dev proxy in dev, the same host in production) and sends the httpOnly session cookie
 * automatically — this file never reads or writes that cookie itself, because JavaScript
 * cannot see it and must not try.
 *
 * Nothing here decides authorization. The server independently enforces every gate and will
 * 403 anything the UI lets through; these functions only shape requests and responses.
 */

export type { SessionUser, UserPublic, ApplicationPublic, GrantPublic, BrandingPublic };

/** Every code the server can send, plus a local fallback for bodies that don't parse. */
export type ClientErrorCode = ApiErrorCode | 'unknown_error';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;

  constructor(status: number, code: ClientErrorCode, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** Narrow helper for the call sites that branch on one specific server refusal. */
export function hasErrorCode(value: unknown, code: ClientErrorCode): boolean {
  return value instanceof ApiError && value.code === code;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const body = value as { error?: unknown };
  if (typeof body.error !== 'object' || body.error === null) return false;
  const error = body.error as { code?: unknown; message?: unknown };
  return typeof error.code === 'string' && typeof error.message === 'string';
}

/**
 * Never assume the error envelope parses — fall back to a generic message when it doesn't.
 *
 * C3 — `reason` rides the SAME envelope and is additive. The server attaches it only when the
 * presented cookie unsigned validly for a real account, so reading it here can never turn an
 * anonymous 401 into an oracle: there is nothing to read on one.
 */
async function readErrorBody(
  response: Response,
): Promise<{ code: ClientErrorCode; message: string; reason?: SessionRefusalReason }> {
  try {
    const data: unknown = await response.json();
    if (isApiErrorBody(data)) {
      return data.error;
    }
  } catch {
    // Body wasn't JSON at all — fall through to the generic message below.
  }
  return { code: 'unknown_error', message: STR.errors.generic };
}

async function throwFromResponse(response: Response): Promise<never> {
  const { code, message, reason } = await readErrorBody(response);
  /*
   * A 401 on a client that held a session means the session was ended for it — R4.
   *
   * EXCEPT `invalid_credentials`, which refuses the credential IN THE BODY — a wrong
   * password re-proof (change password, 2FA disable, recovery-code regeneration) or a wrong
   * TOTP/recovery code — never the session that carried the request: the server leaves that
   * session fully alive. Routing it through the session-lost path would reset the me query,
   * flash the whole app back to its loading screen, and unmount the very dialog whose job
   * is to render the refusal — and on the enrolment screen the remount would mint a fresh
   * secret over a mere typo, stranding the authenticator on a superseded one. Session
   * refusals proper always arrive as `unauthenticated` (with `reason` when the server can
   * name one), and still take the path below.
   */
  if (response.status === 401 && code !== 'invalid_credentials') noteUnauthenticated(reason);
  throw new ApiError(response.status, code, message);
}

async function send(path: string, init: RequestInit = {}): Promise<Response> {
  try {
    return await fetch(path, { ...init, credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'unknown_error', STR.errors.network);
  }
}

export async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const hasJsonBody = typeof init.body === 'string';
  const response = await send(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) await throwFromResponse(response);
  return (await response.json()) as T;
}

/** A POST whose answer is plain text (an IT email draft), with the usual error handling. */
export async function postForText(path: string): Promise<string> {
  const response = await send(path, { method: 'POST', headers: { Accept: 'text/plain' } });
  if (!response.ok) await throwFromResponse(response);
  return response.text();
}

function postJson<T>(path: string, body?: unknown): Promise<T> {
  return requestJson<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

/** Query strings are built in one place so no call site hand-rolls encoding. */
function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered === '' ? '' : `?${rendered}`;
}

/* ------------------------------------------------------------------- branding -- */

/** Public by design: the sign-in page renders branding while signed out. Flat response. */
export function getBranding(): Promise<BrandingPublic> {
  return requestJson<BrandingPublic>('/api/branding');
}

/* -------------------------------------------------------------------- session -- */

export async function getMe(): Promise<SessionUser> {
  const user = await requestJson<SessionUser>('/api/auth/me');
  noteSessionEstablished();
  return user;
}

export function login(body: LoginRequest): Promise<LoginResponse> {
  return postJson<LoginResponse>('/api/auth/login', body);
}

/**
 * `redirect` is a pending destination the wall intercepted. It is passed through untouched
 * and RE-VALIDATED server-side against the live allow-list — the client never turns it into
 * a destination itself, and only ever honours the `redirectTo` the server sends back.
 */
export function changeOwnPassword(body: ChangePasswordRequest): Promise<ChangePasswordResponse> {
  return postJson<ChangePasswordResponse>('/api/auth/me/password', body);
}

export async function logout(): Promise<void> {
  const response = await send('/api/auth/logout', {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });

  // A 401 here means the session was already gone — treat that the same as success, and
  // deliberately do NOT route it through the 401 bounce: this exit was intentional.
  if (response.status === 204 || response.status === 401) return;
  if (!response.ok) await throwFromResponse(response);
}

/**
 * SELF, not somebody else. The admin panel's control on another person is `signOutEverywhere`
 * below; these two are named apart on purpose, because at a call site they are one word from
 * each other and one of them ends the acting session.
 *
 * This one ends EVERY session for the signed-in person including this one, and the server
 * clears the cookie on the response. A 401 is treated exactly as `logout` treats it and for
 * the same reason: already gone is success, and this exit was intentional, so it must not
 * route through the 401 bounce.
 */
export async function signOutEverywhereSelf(): Promise<void> {
  const response = await send('/api/auth/me/sign-out-everywhere', {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });

  if (response.status === 204 || response.status === 401) return;
  if (!response.ok) await throwFromResponse(response);
}

/* ------------------------------------------------------------- two-step (2FA) -- */

/**
 * The login challenge — requires the '2fa-pending' lane the login response issued, which is
 * an httpOnly cookie exactly like the session and equally invisible to this file. A wrong
 * code is 401 `invalid_credentials`; a lapsed or absent pending credential is the uniform
 * 401 `unauthenticated`; repeated misses escalate to 429 `rate_limited`. The success is a
 * LoginResponse (no `twoFactor`): the server has swapped the pending lane for the next one.
 */
export function verifyTwoFactorChallenge(body: TwoFactorVerifyRequest): Promise<LoginResponse> {
  return postJson<LoginResponse>('/api/auth/2fa/verify', body);
}

/** The lost-phone path: spend a single-use recovery code instead. Same lane, same errors. */
export function redeemTwoFactorRecoveryCode(
  body: TwoFactorRecoveryRequest,
): Promise<TwoFactorRecoveryResponse> {
  return postJson<TwoFactorRecoveryResponse>('/api/auth/2fa/recovery', body);
}

/**
 * Begin enrollment. THE ONE SANCTIONED SURFACE for the plaintext TOTP secret: this response,
 * once — held in component state, rendered as a QR and a manual-entry key, and never
 * persisted anywhere by this client. Accepted for a full session (self-service) or the
 * '2fa-pending' lane (held at login by the operator's requirement); re-beginning replaces
 * the pending secret; 409 `conflict` once 2FA is already enabled.
 */
export function beginTwoFactorEnrollment(): Promise<TwoFactorEnrollBeginResponse> {
  return postJson<TwoFactorEnrollBeginResponse>('/api/auth/2fa/enroll');
}

/**
 * Confirm enrollment with a code from the authenticator. THE ONE SANCTIONED SURFACE for the
 * recovery codes' plaintext: this response, once — the codes live in component state until
 * dismissal and nowhere else. The response also carries the browser's next credential (set
 * as a cookie by the server): `user.mustChangePassword` says whether the wall still stands,
 * exactly as at login.
 */
export function confirmTwoFactorEnrollment(
  body: TwoFactorEnrollConfirmRequest,
): Promise<TwoFactorEnrollConfirmResponse> {
  return postJson<TwoFactorEnrollConfirmResponse>('/api/auth/2fa/enroll/confirm', body);
}

/** The signed-in person's OWN 2FA state. No user parameter exists — self only, by design. */
export function getMyTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
  return requestJson<TwoFactorStatusResponse>('/api/auth/me/2fa');
}

/**
 * Turn two-step off. Proof is the account PASSWORD in the body — the first factor, which a
 * hijacked session does not carry and which survives a lost authenticator. A wrong password
 * is 401 `invalid_credentials`; the session itself survives that refusal.
 */
export function disableTwoFactor(body: TwoFactorDisableRequest): Promise<TwoFactorDisableResponse> {
  return postJson<TwoFactorDisableResponse>('/api/auth/me/2fa/disable', body);
}

/**
 * Regenerate recovery codes — same password proof as disable, same shown-once law as
 * enrollment's confirm: the new batch exists in this response and component state only.
 */
export function regenerateTwoFactorRecoveryCodes(
  body: TwoFactorRegenerateRecoveryCodesRequest,
): Promise<TwoFactorRegenerateRecoveryCodesResponse> {
  return postJson<TwoFactorRegenerateRecoveryCodesResponse>('/api/auth/me/2fa/recovery-codes', body);
}

/**
 * The sign-in page's continue-to lookup. Public by design — it is asked while signed out —
 * and rate-limited by IP, so a 429 is an ordinary outcome. It carries a display name and
 * nothing else; the caller never learns whether an unnamed target was unregistered,
 * deactivated or unparseable, and does not need to.
 */
export function getRedirectTarget(redirect: string): Promise<RedirectTargetResponse> {
  return requestJson<RedirectTargetResponse>(`/api/auth/redirect-target${query({ redirect })}`);
}

/* -------------------------------------------------------------- sessions (self) -- */

/**
 * The signed-in person's own devices — every live credential, current one included.
 * Self-only by construction: there is no user parameter, matching the server's route.
 */
export function listMySessions(): Promise<MySessionsResponse> {
  return requestJson<MySessionsResponse>('/api/auth/me/sessions');
}

/**
 * Ends ONE of the caller's own devices, by session id. A 204 is the only success shape —
 * there is no body to parse. Ownership is enforced server-side; a foreign or unknown id
 * answers a uniform 404, never a session-existence oracle.
 */
export async function signOutMySession(sessionId: string): Promise<void> {
  const response = await send(`/api/auth/me/sessions/${encodeURIComponent(sessionId)}/sign-out`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) await throwFromResponse(response);
}

/** Ends every OTHER live device for the caller. The current device is never touched. */
export async function signOutMyOtherSessions(): Promise<void> {
  const response = await send('/api/auth/me/sessions/sign-out-others', {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) await throwFromResponse(response);
}

/* ------------------------------------------------------------------- launcher -- */

/**
 * The signed-in person's OWN launcher list. There is no user parameter: an administrator
 * reading somebody else's access is `GET /api/admin/grants`, behind the admin gate.
 *
 * Live grants to ACTIVE applications only — the server filters both, so there is no `active`
 * field on the rows and nothing for the client to filter. An empty array is a legitimate 200.
 */
export function listMyApplications(): Promise<LauncherApplicationsResponse> {
  return requestJson<LauncherApplicationsResponse>('/api/me/applications');
}

/**
 * The launcher's announcements — ACTIVE only, newest first, filtered and ordered by the
 * SERVER; the client never re-sorts. The rows deliberately carry no author identity and no
 * `active` flag, and an empty array is a legitimate 200: nothing posted is not an error.
 */
export function listMyAnnouncements(): Promise<LauncherAnnouncementsResponse> {
  return requestJson<LauncherAnnouncementsResponse>('/api/me/announcements');
}

/**
 * The launcher layout preference — PER-USER and SERVER-SIDE by ruling, so it follows the
 * person across browsers. Deliberately NOT where palette/mode live (localStorage, per
 * device, and staying there: the sign-in page renders for visitors with no user row).
 *
 * Writes the SESSION'S OWN row: there is no userId parameter on the endpoint and there
 * never will be one. The response is the stored value — the round-trip proof.
 */
export function updateMyPreferences(
  body: UpdateMyPreferencesRequest,
): Promise<MyPreferencesResponse> {
  return requestJson<MyPreferencesResponse>('/api/me/preferences', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/* ---------------------------------------------------------------- admin: users -- */

export interface ListUsersParams {
  q?: string;
  active?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * TWO DENOMINATORS, and they are not interchangeable. `meta.total` counts the rows matching
 * the CURRENT filters and pairs with limit/offset; `meta.summary` is unfiltered and
 * collection-wide, so a header built from it stays a stable directory summary rather than
 * shifting as somebody types in the search box. Neither may be derived from `data`, which is
 * capped and therefore lies for any collection larger than the cap.
 */
export function listUsers(
  params: ListUsersParams = {},
): Promise<PaginatedWithSummary<UserPublic, UsersSummary>> {
  return requestJson<PaginatedWithSummary<UserPublic, UsersSummary>>(
    `/api/admin/users${query({ ...params })}`,
  );
}

export function createUser(body: CreateUserRequest): Promise<CreateUserResponse> {
  return postJson<CreateUserResponse>('/api/admin/users', body);
}

/**
 * C6 — the one write behind the Edit user dialog. `isAdmin` travels with the rest of the
 * form and the SERVER routes it into its guarded admin-toggle path, so the last-active-admin
 * rule is enforced in exactly one place and the client never pre-computes who the last
 * administrator is. `title` / `department` are display-only directory metadata.
 */
export function updateUser(id: string, body: UpdateUserRequest): Promise<UserResponse> {
  return requestJson<UserResponse>(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deactivateUser(id: string): Promise<UserResponse> {
  return postJson<UserResponse>(`/api/admin/users/${encodeURIComponent(id)}/deactivate`);
}

export function reactivateUser(id: string): Promise<UserResponse> {
  return postJson<UserResponse>(`/api/admin/users/${encodeURIComponent(id)}/reactivate`);
}

export function resetUserPassword(id: string): Promise<ResetPasswordResponse> {
  return postJson<ResetPasswordResponse>(
    `/api/admin/users/${encodeURIComponent(id)}/reset-password`,
  );
}

export function signOutEverywhere(id: string): Promise<UserResponse> {
  return postJson<UserResponse>(
    `/api/admin/users/${encodeURIComponent(id)}/sign-out-everywhere`,
  );
}

/**
 * The admin sibling of the operator CLI's `reset-2fa` — the SAME server-side service, so the
 * effects are identical: the secret is cleared (pending enrollments included), two-step is
 * turned off, the recovery codes are superseded, and `session_version` is bumped — a reset
 * ENDS THE TARGET'S LIVE SESSIONS, including the acting administrator's own on a
 * self-target. Idempotent: a target with no 2FA state answers 200 and changes nothing.
 */
export function resetUserTwoFactor(id: string): Promise<UserResponse> {
  return postJson<UserResponse>(`/api/admin/users/${encodeURIComponent(id)}/reset-2fa`);
}

/* -------------------------------------------------------------- admin: sessions -- */

/**
 * The WHOLE live set, grouped by person. No pagination and no `q` parameter — search is
 * client-side at this population, by ruling. Ordering (group order, and current-device-first
 * within a group) is already correct from the server; this client never re-sorts.
 */
export function listAdminSessions(): Promise<AdminSessionsResponse> {
  return requestJson<AdminSessionsResponse>('/api/admin/sessions');
}

/**
 * Ends one device belonging to any user, by session id. The acting administrator's OWN
 * current session answers 409 `conflict` — the UI never offers that affordance (see the
 * Sessions screen), but the server refuses independently either way. The group-level
 * "sign out of all" is deliberately NOT here: it reuses the existing `signOutEverywhere`.
 */
export async function adminSignOutSession(sessionId: string): Promise<void> {
  const response = await send(`/api/admin/sessions/${encodeURIComponent(sessionId)}/sign-out`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) await throwFromResponse(response);
}

/* --------------------------------------------------------- admin: applications -- */

export interface ListApplicationsParams {
  active?: boolean;
  limit?: number;
  offset?: number;
}

/** Same two denominators as `listUsers` — see the note there. */
export function listApplications(
  params: ListApplicationsParams = {},
): Promise<PaginatedWithSummary<ApplicationPublic, ApplicationsSummary>> {
  return requestJson<PaginatedWithSummary<ApplicationPublic, ApplicationsSummary>>(
    `/api/admin/applications${query({ ...params })}`,
  );
}

export function createApplication(
  body: CreateApplicationRequest,
): Promise<ApplicationResponse> {
  return postJson<ApplicationResponse>('/api/admin/applications', body);
}

/**
 * `key` is IMMUTABLE after create (R5) — `UpdateApplicationRequest` has no `key` field and
 * the server returns 400 `immutable_field` if one arrives, so the edit modal never sends it.
 */
export function updateApplication(
  id: string,
  body: UpdateApplicationRequest,
): Promise<ApplicationResponse> {
  return requestJson<ApplicationResponse>(`/api/admin/applications/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Issues a new ACTIVE credential. Used both to issue an application's FIRST credential and
 * to rotate one — the two are the same call; only the caller's copy differs. The plaintext
 * `secret` in the response is THE ONE SANCTIONED SURFACE for it: shown once, never returned
 * again by any endpoint, and this client never persists it anywhere.
 */
export function issueApplicationCredential(
  id: string,
): Promise<IssueApplicationCredentialResponse> {
  return postJson<IssueApplicationCredentialResponse>(
    `/api/admin/applications/${encodeURIComponent(id)}/credentials`,
  );
}

export function revokeApplicationCredential(
  id: string,
  credentialId: string,
): Promise<ApplicationResponse> {
  return postJson<ApplicationResponse>(
    `/api/admin/applications/${encodeURIComponent(id)}/credentials/${encodeURIComponent(credentialId)}/revoke`,
  );
}

export function revokeAllApplicationCredentials(id: string): Promise<ApplicationResponse> {
  return postJson<ApplicationResponse>(
    `/api/admin/applications/${encodeURIComponent(id)}/credentials/revoke-all`,
  );
}

/**
 * Answers 200 for EVERY outcome, including a wrong or revoked secret — the outcome is DATA,
 * never an error, so it never throws `ApiError` for a bad credential. Only a genuine request
 * failure (network, or 400 on an empty secret) reaches the caller as a rejection.
 */
export function testApplicationConnection(
  id: string,
  body: TestApplicationConnectionRequest,
): Promise<TestApplicationConnectionResponse> {
  return postJson<TestApplicationConnectionResponse>(
    `/api/admin/applications/${encodeURIComponent(id)}/test-connection`,
    body,
  );
}

export function checkApplicationNow(id: string): Promise<ApplicationResponse> {
  return postJson<ApplicationResponse>(
    `/api/admin/applications/${encodeURIComponent(id)}/check-now`,
  );
}

/**
 * The per-application launcher logo — the branding logo surface mirrored, with one ruled
 * difference: PNG ONLY. The server sniffs the magic bytes rather than trusting the declared
 * type, so a renamed file is refused server-side whatever this client sends. Multipart: the
 * browser sets its own boundary, so no Content-Type header here.
 */
export async function uploadApplicationLogo(id: string, file: File): Promise<ApplicationPublic> {
  const form = new FormData();
  form.append('file', file);
  const response = await send(`/api/admin/applications/${encodeURIComponent(id)}/logo`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: form,
  });
  if (!response.ok) await throwFromResponse(response);
  return ((await response.json()) as ApplicationResponse).application;
}

/** Deliberately a POST — this codebase has no DELETE verb. The pointer clears; bytes stay. */
export async function removeApplicationLogo(id: string): Promise<ApplicationPublic> {
  const response = await postJson<ApplicationResponse>(
    `/api/admin/applications/${encodeURIComponent(id)}/logo/remove`,
  );
  return response.application;
}

/*
 * THE PER-APPLICATION COLOUR ENDPOINTS ARE GONE (U2, operator ruling). An application's
 * colour is `ApplicationPublic.pillColor`, a column on the application row, written through
 * `updateApplication` above alongside description and tags — one application, one place —
 * and READ from wherever the application already appears: `announcement.application.color`
 * on the admin announcements list, `application.color` on the launcher's announcements, and
 * `apps[].color` on both Sessions reads. There is deliberately nothing left here to call.
 */

/* --------------------------------------------------------------- admin: grants -- */

/** Exactly one of `userId` / `applicationId` is required by the server. */
export type ListGrantsParams =
  | { userId: string; includeRevoked?: boolean }
  | { applicationId: string; includeRevoked?: boolean };

/**
 * Returns the WHOLE envelope, not just `data`. The query is always scoped to one user or one
 * application, so `meta.total` equals the returned row count — but `meta.summary` is the
 * collection-wide live/revoked split, which no caller could compute from a scoped page.
 */
export function listGrants(params: ListGrantsParams): Promise<GrantListResponse> {
  const search = query({
    ...('userId' in params ? { userId: params.userId } : { applicationId: params.applicationId }),
    includeRevoked: params.includeRevoked === true ? 'true' : 'false',
  });
  return requestJson<GrantListResponse>(`/api/admin/grants${search}`);
}

export function createGrant(body: CreateGrantRequest): Promise<{ grant: GrantPublic }> {
  return postJson<{ grant: GrantPublic }>('/api/admin/grants', body);
}

export function revokeGrant(id: string): Promise<{ grant: GrantPublic }> {
  return postJson<{ grant: GrantPublic }>(`/api/admin/grants/${encodeURIComponent(id)}/revoke`);
}

/** Grants one application to every currently active user. `considered = granted + alreadyHeld`. */
export function bulkGrant(body: BulkGrantRequest): Promise<BulkGrantResponse> {
  return postJson<BulkGrantResponse>('/api/admin/grants/bulk', body);
}

/* -------------------------------------------------------- admin: announcements -- */

/** EVERY announcement, active and retired, newest first. Unpaginated: the set is small. */
export function listAnnouncements(): Promise<AnnouncementListResponse> {
  return requestJson<AnnouncementListResponse>('/api/admin/announcements');
}

/**
 * `createdBy` is NEVER sent: the author is the acting session, resolved server-side, and
 * the strict schema refuses the key outright. A new announcement is live immediately.
 */
export function createAnnouncement(
  body: CreateAnnouncementRequest,
): Promise<AnnouncementResponse> {
  return postJson<AnnouncementResponse>('/api/admin/announcements', body);
}

/** Edits the text, never the authorship — there is no author field to send. */
export function updateAnnouncement(
  id: string,
  body: UpdateAnnouncementRequest,
): Promise<AnnouncementResponse> {
  return requestJson<AnnouncementResponse>(
    `/api/admin/announcements/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

/**
 * D5 — RETIRE, NEVER DELETE. These two are the only way an announcement leaves (or returns
 * to) the launcher; there is no delete endpoint and this client offers no delete affordance.
 * Both are idempotent 200s.
 */
export function deactivateAnnouncement(id: string): Promise<AnnouncementResponse> {
  return postJson<AnnouncementResponse>(
    `/api/admin/announcements/${encodeURIComponent(id)}/deactivate`,
  );
}

/**
 * Reactivation is REFUSED (400 `validation_error`) when the announcement's own end time is
 * already past: it would "succeed" into permanent invisibility. The screen surfaces the
 * server's sentence and offers the schedule editor — it never pre-empts the refusal by
 * re-deriving the window itself.
 */
export function reactivateAnnouncement(id: string): Promise<AnnouncementResponse> {
  return postJson<AnnouncementResponse>(
    `/api/admin/announcements/${encodeURIComponent(id)}/reactivate`,
  );
}

/* --------------------------------------------------- admin: announcement types -- */

/** EVERY type, live and retired — a retired type renders forever on the history carrying it. */
export function listAnnouncementTypes(): Promise<AnnouncementTypeListResponse> {
  return requestJson<AnnouncementTypeListResponse>('/api/admin/announcement-types');
}

export function createAnnouncementType(
  body: CreateAnnouncementTypeRequest,
): Promise<AnnouncementTypeResponse> {
  return postJson<AnnouncementTypeResponse>('/api/admin/announcement-types', body);
}

/**
 * Rename and/or recolor. REFUSED on a retired type (400 `inactive_target`) — history renders
 * with the type as it was retired — so this screen offers the control on live types only and
 * still lets the server's own refusal render if one arrives.
 */
export function updateAnnouncementType(
  id: string,
  body: UpdateAnnouncementTypeRequest,
): Promise<AnnouncementTypeResponse> {
  return requestJson<AnnouncementTypeResponse>(
    `/api/admin/announcement-types/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

/** RETIRE, NEVER DELETE — and there is deliberately no restore endpoint for a type (D77). */
export function retireAnnouncementType(id: string): Promise<AnnouncementTypeResponse> {
  return postJson<AnnouncementTypeResponse>(
    `/api/admin/announcement-types/${encodeURIComponent(id)}/retire`,
  );
}

/* ----------------------------------------------------- admin: application tags -- */

/** EVERY tag, live and retired. Unpaginated: the vocabulary is inherently small. */
export function listApplicationTags(): Promise<ApplicationTagListResponse> {
  return requestJson<ApplicationTagListResponse>('/api/admin/application-tags');
}

export function createApplicationTag(
  body: CreateApplicationTagRequest,
): Promise<ApplicationTagResponse> {
  return postJson<ApplicationTagResponse>('/api/admin/application-tags', body);
}

/**
 * Rename — allowed on a retired tag too, unlike an announcement type. Tags are RESTORABLE,
 * so a retired tag's name is still its editors' to shape (the deliberate D77 asymmetry).
 */
export function updateApplicationTag(
  id: string,
  body: UpdateApplicationTagRequest,
): Promise<ApplicationTagResponse> {
  return requestJson<ApplicationTagResponse>(
    `/api/admin/application-tags/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export function retireApplicationTag(id: string): Promise<ApplicationTagResponse> {
  return postJson<ApplicationTagResponse>(
    `/api/admin/application-tags/${encodeURIComponent(id)}/retire`,
  );
}

/** Unlike announcement types, a tag comes back — restore re-checks live-name uniqueness. */
export function restoreApplicationTag(id: string): Promise<ApplicationTagResponse> {
  return postJson<ApplicationTagResponse>(
    `/api/admin/application-tags/${encodeURIComponent(id)}/restore`,
  );
}

/* ---------------------------------------------------------------- admin: audit -- */

export interface ListAuditParams {
  /** A GROUP, resolved server-side — never a client-side prefix match. Omitted for "all". */
  group?: AuditGroup;
  /** One target's rows only — an access record's or catalog row's History is exactly this. */
  targetId?: string;
  /** Free text over actor and target. */
  q?: string;
  /** ISO 8601 WITH an offset. A bare `datetime-local` value is refused with a 400. */
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

/**
 * The audit read. ONE DENOMINATOR here, unlike the users and applications lists: this
 * endpoint carries no `meta.summary`, so `meta.total` — the rows matching the CURRENT
 * filters — is the only count there is, and the screen's result count is that number.
 * `data.length` is capped at `limit` and therefore lies for any filtered set above the cap.
 */
export function listAudit(params: ListAuditParams = {}): Promise<Paginated<AuditEntryPublic>> {
  return requestJson<Paginated<AuditEntryPublic>>(`/api/admin/audit${query({ ...params })}`);
}

/** The list's filters and NOTHING else — the export takes no pagination, by design. */
export type ExportAuditParams = Omit<ListAuditParams, 'limit' | 'offset'>;

/**
 * The WHOLE filtered set in one response, or a refusal. The server enforces its export cap
 * and answers 400 `export_too_large` — naming the matching count AND the cap in its message
 * — rather than truncating; `limit`/`offset` are refused by name, so there is nothing here
 * for a caller to page with. On a 200, `meta.total` always equals `data.length`.
 */
export function exportAudit(params: ExportAuditParams = {}): Promise<AuditExportResponse> {
  return requestJson<AuditExportResponse>(`/api/admin/audit/export${query({ ...params })}`);
}

/* --------------------------------------------------------------- admin: access -- */
/*
 * Access wave 1 (SIR-7460 plan 3a, PR #1). Reads are `user-manager` (admin OR HR); the
 * hierarchy and catalog writes are `admin`. HR's narrower scope (give access only on rows
 * flagged `hrCanGrant`) is the SERVER's to enforce — the screens only leave out what would
 * 403. No delete exists anywhere here: retire, never delete (D5). The request bodies mirror
 * the server's zod schemas field for field; a field the server does not accept is refused
 * there as `validation_failed` (the schemas are `.strict()`).
 */

export interface AccessWorkbookDownload {
  readonly blob: Blob;
  readonly filename: string;
}

const ACCESS_WORKBOOK_FILENAME = 'accounts-access-matrix.xlsx';

/**
 * Prefer the server's RFC 5987 filename, then its quoted/plain filename. A response header
 * is still untrusted text: reduce it to a single safe basename before handing it to the
 * browser's download attribute.
 */
export function accessWorkbookFilename(contentDisposition: string | null): string {
  if (contentDisposition === null) return ACCESS_WORKBOOK_FILENAME;

  const encoded = /(?:^|;)\s*filename\*\s*=\s*UTF-8''([^;]*)/i.exec(contentDisposition)?.[1];
  const plain = /(?:^|;)\s*filename\s*=\s*(?:"([^"]*)"|([^;]*))/i.exec(
    contentDisposition,
  );

  let candidate = plain?.[1] ?? plain?.[2]?.trim();
  if (encoded !== undefined) {
    try {
      candidate = decodeURIComponent(encoded.trim());
    } catch {
      // Keep the usable plain filename when the extended value is malformed.
    }
  }

  const safe = candidate
    ?.split(/[\\/]/)
    .at(-1)
    ?.replace(/[\u0000-\u001f\u007f]/g, '')
    .trim();
  return safe === undefined || safe === '' ? ACCESS_WORKBOOK_FILENAME : safe;
}

/** The full server-built workbook. Matrix view filters never change this request. */
export async function exportAccessWorkbook(): Promise<AccessWorkbookDownload> {
  const response = await send('/api/admin/access/export', {
    method: 'POST',
    headers: {
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });

  if (!response.ok) await throwFromResponse(response);
  return {
    blob: await response.blob(),
    filename: accessWorkbookFilename(response.headers.get('Content-Disposition')),
  };
}

export function setUserHr(id: string, isHr: boolean): Promise<UserResponse> {
  return postJson<UserResponse>(`/api/admin/users/${encodeURIComponent(id)}/hr`, { isHr });
}

export function setUserIt(id: string, isIt: boolean): Promise<UserResponse> {
  return postJson<UserResponse>(`/api/admin/users/${encodeURIComponent(id)}/it`, { isIt });
}

export function listAccessLevels(): Promise<AccessLevelListResponse> {
  return requestJson<AccessLevelListResponse>('/api/admin/access/levels');
}

export function createAccessLevel(name: string): Promise<AccessLevelResponse> {
  return postJson<AccessLevelResponse>('/api/admin/access/levels', { name });
}

export function renameAccessLevel(id: string, name: string): Promise<AccessLevelResponse> {
  return requestJson<AccessLevelResponse>(`/api/admin/access/levels/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export function listAccessNodes(
  params: { includeRetired?: boolean } = {},
): Promise<AccessNodeListResponse> {
  return requestJson<AccessNodeListResponse>(
    `/api/admin/access/nodes${query({ includeRetired: params.includeRetired === true ? 'true' : undefined })}`,
  );
}

export interface CreateAccessNodeBody {
  name: string;
  levelId: string;
  /** Null = a root node ("Global", "Office Tools"). */
  parentId: string | null;
}

export function createAccessNode(body: CreateAccessNodeBody): Promise<AccessNodeResponse> {
  return postJson<AccessNodeResponse>('/api/admin/access/nodes', body);
}

/** Rename and/or move. A node's level never changes. */
export interface UpdateAccessNodeBody {
  name?: string;
  parentId?: string | null;
}

export function updateAccessNode(id: string, body: UpdateAccessNodeBody): Promise<AccessNodeResponse> {
  return requestJson<AccessNodeResponse>(`/api/admin/access/nodes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function retireAccessNode(id: string): Promise<AccessNodeResponse> {
  return postJson<AccessNodeResponse>(`/api/admin/access/nodes/${encodeURIComponent(id)}/retire`);
}

export function listAccessCatalog(
  params: { includeRetired?: boolean; nodeId?: string } = {},
): Promise<AccessCatalogListResponse> {
  return requestJson<AccessCatalogListResponse>(
    `/api/admin/access/catalog${query({
      includeRetired: params.includeRetired === true ? 'true' : undefined,
      nodeId: params.nodeId,
    })}`,
  );
}

export interface CreateAccessCatalogItemBody {
  name: string;
  nodeId: string | null;
  /** The Connected app; null = "None (outside system)". */
  applicationId: string | null;
  requestable: boolean;
  hrCanGrant: boolean;
}

export function createAccessCatalogItem(
  body: CreateAccessCatalogItemBody,
): Promise<AccessCatalogItemResponse> {
  return postJson<AccessCatalogItemResponse>('/api/admin/access/catalog', body);
}

export type UpdateAccessCatalogItemBody = Partial<CreateAccessCatalogItemBody>;

export function updateAccessCatalogItem(
  id: string,
  body: UpdateAccessCatalogItemBody,
): Promise<AccessCatalogItemResponse> {
  return requestJson<AccessCatalogItemResponse>(
    `/api/admin/access/catalog/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export function retireAccessCatalogItem(id: string): Promise<AccessCatalogItemResponse> {
  return postJson<AccessCatalogItemResponse>(
    `/api/admin/access/catalog/${encodeURIComponent(id)}/retire`,
  );
}

export function listAccessRecords(
  params: { userId?: string; catalogItemId?: string; includeRemoved?: boolean } = {},
): Promise<AccessRecordListResponse> {
  return requestJson<AccessRecordListResponse>(
    `/api/admin/access/records${query({
      userId: params.userId,
      catalogItemId: params.catalogItemId,
      includeRemoved: params.includeRemoved === true ? 'true' : undefined,
    })}`,
  );
}

/** `removed` is not settable: it is reached only through `removeAccessRecord`. */
export type SettableAccessRecordStatus = Exclude<AccessRecordStatus, 'removed'>;

export interface CreateAccessRecordBody {
  userId: string;
  catalogItemId: string;
  /** An email or a username — NEVER a password. */
  login: string | null;
  caseLogin: string | null;
  status: SettableAccessRecordStatus;
  passwordHandling: AccessPasswordHandling;
  notes: string | null;
}

export function createAccessRecord(body: CreateAccessRecordBody): Promise<AccessRecordResponse> {
  return postJson<AccessRecordResponse>('/api/admin/access/records', body);
}

export interface UpdateAccessRecordBody {
  login?: string | null;
  caseLogin?: string | null;
  notes?: string | null;
  status?: SettableAccessRecordStatus;
  passwordHandling?: AccessPasswordHandling;
}

export function updateAccessRecord(
  id: string,
  body: UpdateAccessRecordBody,
): Promise<AccessRecordResponse> {
  return requestJson<AccessRecordResponse>(`/api/admin/access/records/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** The access is gone (IT took it away): status `removed`, kept with its history. */
export function removeAccessRecord(id: string): Promise<AccessRecordResponse> {
  return postJson<AccessRecordResponse>(
    `/api/admin/access/records/${encodeURIComponent(id)}/remove`,
  );
}

/** Entered in error: retired, never deleted. */
export function retireAccessRecord(id: string): Promise<AccessRecordResponse> {
  return postJson<AccessRecordResponse>(
    `/api/admin/access/records/${encodeURIComponent(id)}/retire`,
  );
}

export function listAccessRequests(
  params: { status?: AccessRequestStatus; userId?: string } = {},
): Promise<AccessRequestListResponse> {
  return requestJson<AccessRequestListResponse>(
    `/api/admin/access/requests${query({ status: params.status, userId: params.userId })}`,
  );
}

export interface CreateOnBehalfAccessRequestBody {
  userId: string;
  catalogItemId: string;
}

/**
 * A request filed for someone else (`source: on_behalf`), for an administrator to decide.
 * HR's Add access uses it on rows not flagged "HR can grant directly". The server body is
 * strict, so only the two ids are sent.
 */
export function createOnBehalfAccessRequest(
  body: CreateOnBehalfAccessRequestBody,
): Promise<AccessRequestResponse> {
  return postJson<AccessRequestResponse>('/api/admin/access/requests', {
    userId: body.userId,
    catalogItemId: body.catalogItemId,
  });
}

/**
 * Approving an app-bound request IS `createGrant`; an outside one creates an access record
 * (status `needs_access`) with the password handling picked before Approve — on the
 * approval itself, so the record and the approval audit row carry it in one write. Omit
 * `passwordHandling` for a connected-app row: the server refuses one there.
 */
export function approveAccessRequest(
  id: string,
  passwordHandling?: AccessPasswordHandling,
): Promise<AccessRequestDecisionResponse> {
  return postJson<AccessRequestDecisionResponse>(
    `/api/admin/access/requests/${encodeURIComponent(id)}/approve`,
    passwordHandling === undefined ? undefined : { passwordHandling },
  );
}

/** A deny note is required by the server. */
export function denyAccessRequest(id: string, note: string): Promise<AccessRequestDecisionResponse> {
  return postJson<AccessRequestDecisionResponse>(
    `/api/admin/access/requests/${encodeURIComponent(id)}/deny`,
    { note },
  );
}

export interface AccessBulkBody {
  action: AccessBulkAction;
  /** Request ids for approve / deny; access-record ids for mark_granted / mark_removed. */
  ids: string[];
  /** Required for deny. */
  note?: string;
  /**
   * Approve only: password handling per request id, for outside rows. A connected-app id
   * named here is refused; ids left out are approved as `managed`.
   */
  passwordHandlingById?: Record<string, AccessPasswordHandling>;
}

/** One audit row per item, all sharing `bulkId`. At most 200 ids per call. */
export function bulkAccessAction(body: AccessBulkBody): Promise<AccessBulkResponse> {
  return postJson<AccessBulkResponse>('/api/admin/access/requests/bulk', body);
}

export interface AccessQueueParams {
  type?: AccessQueueType;
  nodeId?: string;
  userId?: string;
  /** Person name or email, or login. */
  q?: string;
}

export function getAccessQueue(params: AccessQueueParams = {}): Promise<AccessQueueResponse> {
  return requestJson<AccessQueueResponse>(`/api/admin/access/queue${query({ ...params })}`);
}

/* -------------------------------------------------------------- self: access -- */

/** Live, requestable catalog rows only — what Request access offers. */
export function listMyRequestableCatalog(): Promise<AccessCatalogListResponse> {
  return requestJson<AccessCatalogListResponse>('/api/me/access/catalog');
}

export function listMyAccessRequests(): Promise<AccessRequestListResponse> {
  return requestJson<AccessRequestListResponse>('/api/me/access/requests');
}

/** `note` is sent only when there is one: the server refuses `note: null`. */
export function createMyAccessRequest(
  catalogItemId: string,
  note?: string,
): Promise<AccessRequestResponse> {
  return postJson<AccessRequestResponse>(
    '/api/me/access/requests',
    note === undefined ? { catalogItemId } : { catalogItemId, note },
  );
}

export function cancelMyAccessRequest(id: string): Promise<AccessRequestResponse> {
  return postJson<AccessRequestResponse>(
    `/api/me/access/requests/${encodeURIComponent(id)}/cancel`,
  );
}

/* ------------------------------------------- self: Other requests and answer emails -- */

/** "Something else": a request for something not in the catalog (spec row 13). */
export function createMyOtherRequest(
  body: CreateOtherAccessRequestRequest,
): Promise<OtherAccessRequestResponse> {
  return postJson<OtherAccessRequestResponse>('/api/me/access/other-requests', body);
}

export function listMyOtherRequests(): Promise<OtherAccessRequestListResponse> {
  return requestJson<OtherAccessRequestListResponse>('/api/me/access/other-requests');
}

export function cancelMyOtherRequest(id: string): Promise<OtherAccessRequestResponse> {
  return postJson<OtherAccessRequestResponse>(
    `/api/me/access/other-requests/${encodeURIComponent(id)}/cancel`,
  );
}

/** The answer emails for one of the caller's OWN requests, oldest first (spec row 14). */
export function listMyRequestEmails(
  kind: 'catalog' | 'other',
  id: string,
): Promise<AnswerEmailListResponse> {
  const base = kind === 'catalog' ? '/api/me/access/requests' : '/api/me/access/other-requests';
  return requestJson<AnswerEmailListResponse>(`${base}/${encodeURIComponent(id)}/emails`);
}

/* ------------------------------------------------------------------- self: bell -- */

export function listMyNotifications(limit: number): Promise<BellNotificationListResponse> {
  return requestJson<BellNotificationListResponse>(`/api/me/notifications${query({ limit })}`);
}

export function markMyNotificationRead(id: string): Promise<BellNotificationReadResponse> {
  return postJson<BellNotificationReadResponse>(
    `/api/me/notifications/${encodeURIComponent(id)}/read`,
  );
}

export function markAllMyNotificationsRead(): Promise<BellNotificationReadAllResponse> {
  return postJson<BellNotificationReadAllResponse>('/api/me/notifications/read-all');
}

/* ------------------------------------------------------- admin: Other requests -- */

export function listOtherRequests(
  params: { status?: OtherAccessRequestStatus } = {},
): Promise<AdminOtherAccessRequestListResponse> {
  return requestJson<AdminOtherAccessRequestListResponse>(
    `/api/admin/access/other-requests${query({ status: params.status })}`,
  );
}

/** Adds the catalog row and gives it to the person in ONE server transaction. */
export function approveOtherRequest(
  id: string,
  body: ApproveOtherAccessRequestRequest,
): Promise<ApproveOtherAccessRequestResponse> {
  return postJson<ApproveOtherAccessRequestResponse>(
    `/api/admin/access/other-requests/${encodeURIComponent(id)}/approve`,
    body,
  );
}

/** The reason is optional here; the server stores an empty one as null. */
export function declineOtherRequest(
  id: string,
  reason: string | null,
): Promise<OtherAccessRequestResponse> {
  return postJson<OtherAccessRequestResponse>(
    `/api/admin/access/other-requests/${encodeURIComponent(id)}/decline`,
    { reason },
  );
}

/* ------------------------------------------------------------ admin: IT emails -- */

/** Which records are already in a live (draft or sent) IT email — the picker leaves them out. */
export function listItEmailMemberships(): Promise<ItEmailMembershipListResponse> {
  return requestJson<ItEmailMembershipListResponse>('/api/admin/access/it-email-memberships');
}

export function listItEmails(params: { status?: ItEmailStatus } = {}): Promise<ItEmailListResponse> {
  return requestJson<ItEmailListResponse>(`/api/admin/access/it-emails${query({ ...params })}`);
}

/** The server checks each record, picks Set up or Remove, and writes the first text. */
export function createItEmail(recordIds: readonly string[]): Promise<ItEmailResponse> {
  return postJson<ItEmailResponse>('/api/admin/access/it-emails', { recordIds });
}

/** Save text / Save draft. Drafts only. */
export function updateItEmailText(id: string, text: string): Promise<ItEmailResponse> {
  return requestJson<ItEmailResponse>(`/api/admin/access/it-emails/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ text }),
  });
}

export function markItEmailSent(id: string): Promise<ItEmailResponse> {
  return postJson<ItEmailResponse>(`/api/admin/access/it-emails/${encodeURIComponent(id)}/mark-sent`);
}

/** One item: `mark-granted` for a Set up item, `mark-removed` for a Remove item. */
export function completeItEmailItem(
  emailId: string,
  itemId: string,
  action: ItEmailAction,
): Promise<ItEmailResponse> {
  const verb = action === 'set_up' ? 'mark-granted' : 'mark-removed';
  return postJson<ItEmailResponse>(
    `/api/admin/access/it-emails/${encodeURIComponent(emailId)}/items/${encodeURIComponent(itemId)}/${verb}`,
  );
}

export function confirmAllItEmail(id: string): Promise<ItEmailResponse> {
  return postJson<ItEmailResponse>(
    `/api/admin/access/it-emails/${encodeURIComponent(id)}/confirm-all`,
  );
}

/** The whole email: open items go back to their lanes; a draft is retired, never deleted. */
export function takeBackItEmail(id: string): Promise<ItEmailResponse> {
  return postJson<ItEmailResponse>(`/api/admin/access/it-emails/${encodeURIComponent(id)}/take-back`);
}

/* ----------------------------------------------------------------- admin: mail -- */

/** Every answer email in the outbox, newest first; `meta.driver` says whether any left. */
export function listAdminMail(params: { page: number; pageSize: number }): Promise<AdminMailEmailListResponse> {
  return requestJson<AdminMailEmailListResponse>(`/api/admin/mail/emails${query({ ...params })}`);
}

/* ------------------------------------------------------------- admin: branding -- */

export async function updateBranding(body: UpdateBrandingRequest): Promise<BrandingPublic> {
  const response = await requestJson<BrandingResponse>('/api/admin/settings/branding', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return response.branding;
}

/** Multipart: the browser sets its own boundary, so no Content-Type header here. */
export async function uploadBrandingLogo(file: File): Promise<BrandingPublic> {
  const form = new FormData();
  form.append('file', file);
  const response = await send('/api/admin/settings/branding/logo', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: form,
  });
  if (!response.ok) await throwFromResponse(response);
  return ((await response.json()) as BrandingResponse).branding;
}

/** Deliberately a POST — this codebase has no DELETE verb. */
export async function removeBrandingLogo(): Promise<BrandingPublic> {
  const response = await postJson<BrandingResponse>('/api/admin/settings/branding/logo/remove');
  return response.branding;
}

/* ---------------------------------------------------- admin: operational settings -- */

/**
 * Ruling 1 — health-poll cadence and failure tolerance, admin-gated (unlike `branding`,
 * which is public). Bounds (15–3600s, 1–10 failures) are enforced SERVER-SIDE, independently;
 * this client never assumes its own affordance bounds are the whole story.
 */
export function getOperationalSettings(): Promise<OperationalSettingsResponse> {
  return requestJson<OperationalSettingsResponse>('/api/admin/settings/operational');
}

export function updateOperationalSettings(
  body: UpdateOperationalSettingsRequest,
): Promise<OperationalSettingsResponse> {
  return requestJson<OperationalSettingsResponse>('/api/admin/settings/operational', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/* --------------------------------------------------------- admin: access settings -- */

/** The access module's own settings (the app-deactivation overdue threshold). Admin only. */
export function getAccessSettings(): Promise<AccessSettingsResponse> {
  return requestJson<AccessSettingsResponse>('/api/admin/access/settings');
}

export function updateAccessSettings(
  body: UpdateAccessSettingsRequest,
): Promise<AccessSettingsResponse> {
  return requestJson<AccessSettingsResponse>('/api/admin/access/settings', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/* ---------------------------------------------------------- admin: email settings -- */

/*
 * SIR-7804 — the email settings contract as Suresh posted it on SIR-7804 (19 Oct). These
 * shapes MIRROR the backend until its PR lands them in `@accounts/shared`; then they are
 * deleted here and imported from there, so the contract keeps one home.
 */
export type EmailDriver = 'capture' | 'sendgrid';

export interface EmailEventSettingPublic {
  key: string;
  /** Display text from the server: the client never derives a label from a key. */
  label: string;
  emailEnabled: boolean;
  inAppEnabled: boolean;
}

export interface EmailSettingsPublic {
  masterEnabled: boolean;
  fromAddress: string;
  senderName: string;
  /** Null means replies go to `fromAddress`. */
  replyTo: string | null;
  events: EmailEventSettingPublic[];
}

/** The effective setup. The key itself, its ciphertext and its nonce are never returned. */
export interface EmailSettingsStatus {
  driver: EmailDriver;
  sendGridApiKeyConfigured: boolean;
  sendGridApiKeyLast4: string | null;
  masterEnabled: boolean;
  fromAddress: string;
}

export interface EmailSettingsResponse {
  settings: EmailSettingsPublic;
  status: EmailSettingsStatus;
}

export interface UpdateEmailSettingsRequest {
  masterEnabled: boolean;
  /** Omitted keeps the stored key; a string replaces it; null removes the stored override. */
  sendGridApiKey?: string | null;
  fromAddress: string;
  senderName: string;
  replyTo: string | null;
  events: { key: string; emailEnabled: boolean; inAppEnabled: boolean }[];
}

export type EmailTestSkipReason = 'capture_mode' | 'master_disabled' | 'api_key_not_configured';

export interface EmailTestResponse {
  result:
    | { status: 'sent'; recipient: string }
    | { status: 'skipped'; recipient: string; reason: EmailTestSkipReason };
}

export function getEmailSettings(): Promise<EmailSettingsResponse> {
  return requestJson<EmailSettingsResponse>('/api/admin/settings/email');
}

export function updateEmailSettings(
  body: UpdateEmailSettingsRequest,
): Promise<EmailSettingsResponse> {
  return requestJson<EmailSettingsResponse>('/api/admin/settings/email', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Always to the signed-in admin's own address: the body carries no recipient. */
export function sendTestEmail(): Promise<EmailTestResponse> {
  return postJson<EmailTestResponse>('/api/admin/settings/email/test', {});
}
